import React from "react";
import { KeyTerm, ImportantNote, Mnemonic } from "../note-components";
import type { UnitNotes } from "../types";

const humanGeographyUnit1Notes: UnitNotes = {
  unitId: "unit-1",
  unitName: "Unit 1: Thinking Geographically",
  examWeight: "8–10%",
  focus: "How geographers see, analyze, and represent the world. This unit lays the foundation for understanding spatial patterns and processes.",
  sections: [
    {
      id: "topic-1-1",
      title: "Topic 1.1: Introduction to Maps",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🗺️ What is Geography?</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              Geography is the study of the <strong>"why of where."</strong> Maps are the primary tool used to display and analyze spatial information.
            </p>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Types of Maps</h3>
            <div className="grid md:grid-cols-2 gap-4 mb-4">
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <h4 className="font-bold text-blue-800 mb-2">📋 Reference Maps</h4>
                <p className="text-sm text-slate-700 mb-2">Designed for general information about places.</p>
                <ul className="text-xs text-slate-600 space-y-1 list-disc pl-4">
                  <li><strong>Political:</strong> Show human-created boundaries</li>
                  <li><strong>Physical:</strong> Show natural features</li>
                  <li><strong>Road:</strong> Show transportation routes</li>
                </ul>
              </div>
              <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                <h4 className="font-bold text-green-800 mb-2">📊 Thematic Maps</h4>
                <p className="text-sm text-slate-700 mb-2">Show spatial aspects of specific phenomena.</p>
                <ul className="text-xs text-slate-600 space-y-1 list-disc pl-4">
                  <li><strong>Choropleth:</strong> Color-coded by data</li>
                  <li><strong>Dot Distribution:</strong> Specific locations</li>
                  <li><strong>Graduated Symbol:</strong> Size indicates value</li>
                </ul>
              </div>
            </div>

            <h4 className="text-lg font-bold text-slate-800 mb-3 mt-6">Thematic Map Types (Detailed)</h4>
            <div className="space-y-3">
              <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
                <h5 className="font-bold text-purple-800">🎨 Choropleth Map</h5>
                <p className="text-sm text-slate-700 mt-1">Uses various colors or shades to show the location and distribution of spatial data (e.g., darker blue for higher population density).</p>
              </div>
              <div className="bg-orange-50 p-4 rounded-lg border border-orange-200">
                <h5 className="font-bold text-orange-800">🔴 Dot Distribution Map</h5>
                <p className="text-sm text-slate-700 mt-1">Shows the specific location and distribution of something. Each dot represents a specified quantity.</p>
              </div>
              <div className="bg-pink-50 p-4 rounded-lg border border-pink-200">
                <h5 className="font-bold text-pink-800">📈 Graduated (Proportional) Symbol Map</h5>
                <p className="text-sm text-slate-700 mt-1">Uses symbols of different sizes to indicate different amounts. Larger icons = higher value. Good for seeing ranking.</p>
              </div>
              <div className="bg-cyan-50 p-4 rounded-lg border border-cyan-200">
                <h5 className="font-bold text-cyan-800">〰️ Isoline Map</h5>
                <p className="text-sm text-slate-700 mt-1">Uses lines connecting points of equal value to depict variations in data across space (e.g., topographic maps for elevation).</p>
              </div>
              <div className="bg-red-50 p-4 rounded-lg border border-red-200">
                <h5 className="font-bold text-red-800">🌍 Cartogram</h5>
                <p className="text-sm text-slate-700 mt-1">Sizes of countries/states are shown according to a statistic (like population or GDP) rather than actual land area.</p>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Map Projections and Distortion</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              Because Earth is a sphere (3D) and maps are flat (2D), <strong>all maps contain distortion.</strong> It is impossible to flatten a globe without skewing one of these spatial relationships:
            </p>
            <Mnemonic 
              title="S.A.D.D." 
              content="Shape, Area (Size), Distance, Direction"
            />

            <h4 className="text-lg font-bold text-slate-800 mb-3 mt-6">Common Projections</h4>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="bg-gradient-to-br from-blue-100 to-blue-50 p-4 rounded-lg border border-blue-200">
                <h5 className="font-bold text-blue-900">🧭 Mercator Projection</h5>
                <p className="text-sm text-slate-700 mt-2"><strong>Utility:</strong> Navigation (direction is accurate)</p>
                <p className="text-sm text-red-700 mt-1"><strong>Distortion:</strong> Area is distorted near poles (Greenland looks as big as Africa)</p>
              </div>
              <div className="bg-gradient-to-br from-green-100 to-green-50 p-4 rounded-lg border border-green-200">
                <h5 className="font-bold text-green-900">⚖️ Peters Projection</h5>
                <p className="text-sm text-slate-700 mt-2"><strong>Utility:</strong> Spatial equity; keeps land masses equal in area</p>
                <p className="text-sm text-red-700 mt-1"><strong>Distortion:</strong> Shapes are highly distorted (Africa looks stretched)</p>
              </div>
              <div className="bg-gradient-to-br from-purple-100 to-purple-50 p-4 rounded-lg border border-purple-200">
                <h5 className="font-bold text-purple-900">🎯 Robinson Projection</h5>
                <p className="text-sm text-slate-700 mt-2"><strong>Utility:</strong> Compromise projection; balances errors. Aesthetically pleasing</p>
                <p className="text-sm text-amber-700 mt-1"><strong>Distortion:</strong> Everything slightly distorted, nothing drastically</p>
              </div>
              <div className="bg-gradient-to-br from-orange-100 to-orange-50 p-4 rounded-lg border border-orange-200">
                <h5 className="font-bold text-orange-900">🧊 Polar (Azimuthal) Projection</h5>
                <p className="text-sm text-slate-700 mt-2"><strong>Utility:</strong> Great Circle Routes (airplanes); viewing poles</p>
                <p className="text-sm text-red-700 mt-1"><strong>Distortion:</strong> Increases as you move away from center point</p>
              </div>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-1-2",
      title: "Topic 1.2: Geographic Data",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Geospatial Technologies</h3>
            <div className="space-y-3">
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <h4 className="font-bold text-blue-800">💻 GIS (Geographic Information Systems)</h4>
                <p className="text-sm text-slate-700 mt-2">A computer system that captures, stores, queries, analyzes, and displays geographic data.</p>
                <div className="bg-blue-100 p-2 rounded mt-2">
                  <p className="text-xs text-blue-800"><strong>Key Feature:</strong> <strong>Layering</strong> - stacks different datasets (roads, vegetation, boundaries) to analyze relationships.</p>
                </div>
                <p className="text-xs text-slate-600 mt-2"><strong>Uses:</strong> Urban planning, crime analysis, disease tracking</p>
              </div>
              <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                <h4 className="font-bold text-green-800">🛰️ GPS (Global Positioning System)</h4>
                <p className="text-sm text-slate-700 mt-2">Determines precise position on Earth through satellites, tracking stations, and receivers.</p>
                <div className="bg-green-100 p-2 rounded mt-2">
                  <p className="text-xs text-green-800"><strong>Key Feature:</strong> <strong>Absolute Location</strong> - exact coordinates</p>
                </div>
                <p className="text-xs text-slate-600 mt-2"><strong>Uses:</strong> Navigation (Google Maps), tracking, geocaching</p>
              </div>
              <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
                <h4 className="font-bold text-purple-800">📡 Remote Sensing</h4>
                <p className="text-sm text-slate-700 mt-2">Acquisition of data about Earth's surface from satellites, drones, or aircraft.</p>
                <div className="bg-purple-100 p-2 rounded mt-2">
                  <p className="text-xs text-purple-800"><strong>Key Feature:</strong> <strong>Scanning the surface</strong> from a distance</p>
                </div>
                <p className="text-xs text-slate-600 mt-2"><strong>Uses:</strong> Environmental monitoring, weather patterns, military</p>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Other Data Sources</h3>
            <div className="grid md:grid-cols-2 gap-3">
              <KeyTerm term="Census Data" definition="Official count of a population (usually every 10 years)." />
              <KeyTerm term="Field Observations" definition="Going to a place and physically observing/taking notes." />
              <KeyTerm term="Interviews/Surveys" definition="Asking people about their perceptions or behaviors." />
              <KeyTerm term="Landscape Analysis" definition="Interpreting the 'cultural landscape' (human impact on environment)." />
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Data Types</h3>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <h4 className="font-bold text-blue-800">📊 Quantitative Data</h4>
                <p className="text-sm text-slate-700 mt-1">Numbers, hard data, statistics (e.g., "The temperature is 85°F")</p>
              </div>
              <div className="bg-orange-50 p-4 rounded-lg border border-orange-200">
                <h4 className="font-bold text-orange-800">📝 Qualitative Data</h4>
                <p className="text-sm text-slate-700 mt-1">Subjective, descriptive, characteristics, opinions (e.g., "It feels hot and humid")</p>
              </div>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-1-3",
      title: "Topic 1.3: The Power of Geographic Data",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Using Data for Decision Making</h3>
            <div className="space-y-3">
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <h4 className="font-bold text-blue-800">🏛️ Government</h4>
                <p className="text-sm text-slate-700 mt-1">Uses census data for redistricting (drawing voting lines) and allocating funding for schools, hospitals, and infrastructure.</p>
              </div>
              <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                <h4 className="font-bold text-green-800">💼 Business</h4>
                <p className="text-sm text-slate-700 mt-1">Uses location data to decide where to open stores (e.g., Starbucks looks for high traffic and high income).</p>
              </div>
              <div className="bg-red-50 p-4 rounded-lg border border-red-200">
                <h4 className="font-bold text-red-800">⚕️ Medical</h4>
                <p className="text-sm text-slate-700 mt-1">Traces disease sources and maps spread patterns (spatial diffusion).</p>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Issues with Data</h3>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
                <h4 className="font-bold text-purple-800">🔒 Privacy</h4>
                <p className="text-sm text-slate-700 mt-1">As GPS tracking becomes ubiquitous, concerns arise about who owns location data.</p>
              </div>
              <div className="bg-orange-50 p-4 rounded-lg border border-orange-200">
                <h4 className="font-bold text-orange-800">⚠️ Bias</h4>
                <p className="text-sm text-slate-700 mt-1">Maps are created by people. Choice of projection, color, or scale can manipulate the message (e.g., using red to imply danger).</p>
              </div>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-1-4",
      title: "Topic 1.4: Spatial Concepts",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Location (The "Where")</h3>
            <div className="grid md:grid-cols-2 gap-4 mb-4">
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <h4 className="font-bold text-blue-800">📍 Absolute Location</h4>
                <p className="text-sm text-slate-700 mt-1">The exact spot on Earth where something is. Usually defined by coordinates (Latitude/Longitude) or street address.</p>
                <p className="text-xs text-blue-600 mt-2 font-semibold">Does NOT change</p>
              </div>
              <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                <h4 className="font-bold text-green-800">🧭 Relative Location</h4>
                <p className="text-sm text-slate-700 mt-1">Where something is in relation to other things (e.g., "next to the library" or "on the southwest shore of Lake Michigan").</p>
                <p className="text-xs text-green-600 mt-2 font-semibold">CAN change if surroundings change</p>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Place vs. Space</h3>
            <div className="grid md:grid-cols-2 gap-4 mb-4">
              <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
                <h4 className="font-bold text-purple-800">📏 Space</h4>
                <p className="text-sm text-slate-700 mt-1">The physical gap or interval between two objects.</p>
              </div>
              <div className="bg-pink-50 p-4 rounded-lg border border-pink-200">
                <h4 className="font-bold text-pink-800">🏠 Place</h4>
                <p className="text-sm text-slate-700 mt-1">A specific point on Earth distinguished by particular character (human and physical characteristics).</p>
              </div>
            </div>

            <div className="space-y-3">
              <div className="bg-amber-50 p-4 rounded-lg border border-amber-200">
                <h5 className="font-bold text-amber-800">💕 Sense of Place</h5>
                <p className="text-sm text-slate-700 mt-1">The emotional attachment humans have to specific locations (Home).</p>
              </div>
              <div className="bg-red-50 p-4 rounded-lg border border-red-200">
                <h5 className="font-bold text-red-800">❌ Placelessness</h5>
                <p className="text-sm text-slate-700 mt-1">When a location loses its uniqueness and looks like everywhere else (e.g., a strip mall with Walmart and McDonald's looks the same in Ohio as in Texas).</p>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Spatial Interaction</h3>
            <div className="space-y-3">
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <h4 className="font-bold text-blue-800">📉 Distance Decay</h4>
                <p className="text-sm text-slate-700 mt-1">The effect of distance on cultural or spatial interactions. The further apart two things are, the less they interact.</p>
              </div>
              <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                <h4 className="font-bold text-green-800">⏱️ Time-Space Compression</h4>
                <p className="text-sm text-slate-700 mt-1">The reduction in time it takes to diffuse something to a distant place due to improved communications and transportation. The world "feels" smaller (internet, airplanes).</p>
              </div>
              <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
                <h4 className="font-bold text-purple-800">🌊 Flows</h4>
                <p className="text-sm text-slate-700 mt-1">The movement of people, goods, or ideas.</p>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Pattern (The Arrangement)</h3>
            <div className="grid md:grid-cols-3 gap-3">
              <div className="bg-red-50 p-4 rounded-lg border border-red-200">
                <h4 className="font-bold text-red-800">🔴 Clustered</h4>
                <p className="text-sm text-slate-700 mt-1">Objects are close together.</p>
              </div>
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <h4 className="font-bold text-blue-800">🔵 Dispersed</h4>
                <p className="text-sm text-slate-700 mt-1">Objects are far apart.</p>
              </div>
              <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                <h4 className="font-bold text-green-800">➡️ Linear</h4>
                <p className="text-sm text-slate-700 mt-1">Objects arranged in a line (along rivers/railroads).</p>
              </div>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-1-5",
      title: "Topic 1.5: Human-Environmental Interaction",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Cultural Ecology</h3>
            <KeyTerm 
              term="Cultural Ecology" 
              definition="The geographic study of human-environment relationships."
            />
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Theories of Interaction</h3>
            <div className="space-y-4">
              <div className="bg-red-50 p-4 rounded-lg border border-red-200">
                <h4 className="font-bold text-red-800">❌ Environmental Determinism</h4>
                <p className="text-sm text-slate-700 mt-2"><strong>Definition:</strong> The 19th-century theory that the physical environment <em>solely</em> causes social development.</p>
                <p className="text-sm text-slate-700 mt-2"><strong>Claim:</strong> Climate and terrain determine human intelligence, culture, and success.</p>
                <p className="text-sm text-red-700 mt-2 font-semibold">Status: REJECTED/DISCREDITED as racist and scientifically inaccurate.</p>
              </div>
              <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                <h4 className="font-bold text-green-800">✅ Possibilism</h4>
                <p className="text-sm text-slate-700 mt-2"><strong>Definition:</strong> The modern theory that the physical environment may set limits on human actions, but people have the ability to adjust and choose from many alternatives.</p>
                <p className="text-sm text-slate-700 mt-2"><strong>Claim:</strong> Humans can overcome environmental limitations through technology (dams, air conditioning, greenhouses, terraced farming).</p>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Sustainability</h3>
            <KeyTerm 
              term="Sustainability" 
              definition="The use of Earth's renewable and nonrenewable natural resources in ways that ensure resource availability for future generations."
            />
            <p className="text-slate-700 leading-relaxed mt-4">
              <strong>Land Use:</strong> How humans modify the land (agriculture, urbanization, deforestation).
            </p>
          </section>
        </div>
      )
    },
    {
      id: "topic-1-6",
      title: "Topic 1.6: Scales of Analysis",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Cartographic Scale (Map Scale)</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              The relationship between the size of an object on a map and the size of the actual feature on Earth's surface.
            </p>
            <div className="grid md:grid-cols-2 gap-4 mb-4">
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <h4 className="font-bold text-blue-800">🌍 Small Scale Map</h4>
                <p className="text-sm text-slate-700 mt-1">Shows a <strong>large area</strong> with <strong>little detail</strong> (e.g., a world map).</p>
              </div>
              <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                <h4 className="font-bold text-green-800">🏙️ Large Scale Map</h4>
                <p className="text-sm text-slate-700 mt-1">Shows a <strong>small area</strong> with <strong>great detail</strong> (e.g., a city center map).</p>
              </div>
            </div>
            <ImportantNote>
              <strong>Tip:</strong> Think of "scale" as the "zoom level."
            </ImportantNote>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Scale of Analysis (The "Zoom" of the Data)</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              This refers to the level at which data is aggregated (grouped) and analyzed. <strong>Patterns change depending on the scale of analysis.</strong>
            </p>
            <div className="space-y-3">
              <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
                <h4 className="font-bold text-purple-800">🌐 Global Scale</h4>
                <p className="text-sm text-slate-700 mt-1">Showing data for the whole world (e.g., climate change).</p>
              </div>
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <h4 className="font-bold text-blue-800">🗺️ Regional Scale</h4>
                <p className="text-sm text-slate-700 mt-1">Showing data by continents or major world regions (e.g., Latin America vs. Europe).</p>
              </div>
              <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                <h4 className="font-bold text-green-800">🏳️ National Scale</h4>
                <p className="text-sm text-slate-700 mt-1">Showing data for one or more countries (e.g., US population).</p>
              </div>
              <div className="bg-orange-50 p-4 rounded-lg border border-orange-200">
                <h4 className="font-bold text-orange-800">📍 Local/Sub-National Scale</h4>
                <p className="text-sm text-slate-700 mt-1">Showing data for states, counties, cities, or census tracts.</p>
              </div>
            </div>
            <ImportantNote>
              <strong>Crucial Concept:</strong> A pattern seen at the national scale (e.g., "The US is wealthy") may not be true at the local scale (e.g., a specific county is impoverished).
            </ImportantNote>
          </section>
        </div>
      )
    },
    {
      id: "topic-1-7",
      title: "Topic 1.7: Regional Analysis",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Types of Regions</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              Geographers group places into regions to make them easier to study.
            </p>

            <div className="space-y-4">
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <h4 className="font-bold text-blue-800">📦 Formal Region (Uniform/Homogeneous)</h4>
                <p className="text-sm text-slate-700 mt-2">An area in which everyone shares one or more distinctive characteristics.</p>
                <p className="text-xs text-blue-700 mt-2"><strong>Borders:</strong> Usually easy to track and distinct</p>
                <p className="text-xs text-slate-600 mt-2"><strong>Examples:</strong> French-speaking Quebec, Florida (political), Sahara Desert (environmental), Corn Belt</p>
              </div>

              <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                <h4 className="font-bold text-green-800">🎯 Functional Region (Nodal)</h4>
                <p className="text-sm text-slate-700 mt-2">An area organized around a node or focal point. The characteristic dominates at the center and diminishes outward (distance decay).</p>
                <p className="text-xs text-green-700 mt-2"><strong>Characteristics:</strong> Tied together by transportation, communication, or economics. If the "node" dies, the region disappears.</p>
                <p className="text-xs text-slate-600 mt-2"><strong>Examples:</strong> TV station reception area, pizza delivery zone, airline hub-and-spoke, city commuter zone</p>
              </div>

              <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
                <h4 className="font-bold text-purple-800">💭 Vernacular Region (Perceptual)</h4>
                <p className="text-sm text-slate-700 mt-2">An area that people <em>believe</em> exists as part of their cultural identity.</p>
                <p className="text-xs text-purple-700 mt-2"><strong>Characteristics:</strong> Borders are fuzzy and subjective; different people draw borders in different places.</p>
                <p className="text-xs text-slate-600 mt-2"><strong>Examples:</strong> "The South" (US), "The Middle East"</p>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Regional Boundaries</h3>
            <ImportantNote>
              Regional boundaries are transitional and often contested and overlapping. They are not static and can change over time.
            </ImportantNote>
          </section>
        </div>
      )
    }
  ]
};

export default humanGeographyUnit1Notes;
